import { useState, useEffect } from 'react';
import { GamificationState, Badge, LearningProgress, StreakData, QuizScore } from '@/types/gamification';

const STORAGE_KEY = 'menopause-gamification';

const initialBadges: Badge[] = [
  // Micro-learning badges
  {
    id: 'first-lesson',
    name: 'Curious Beginner',
    description: 'Completed your first micro-lesson',
    icon: '🌱',
    category: 'learning',
    unlocked: false,
    requirement: 'Complete 1 micro-lesson'
  },
  {
    id: 'lesson-3',
    name: 'Knowledge Sprout',
    description: 'Completed 3 micro-lessons',
    icon: '🌿',
    category: 'learning',
    unlocked: false,
    requirement: 'Complete 3 micro-lessons'
  },
  {
    id: 'lesson-5',
    name: 'Learning Enthusiast',
    description: 'Completed 5 micro-lessons',
    icon: '🍃',
    category: 'learning',
    unlocked: false,
    requirement: 'Complete 5 micro-lessons'
  },
  {
    id: 'lesson-10',
    name: 'Knowledge Seeker',
    description: 'Completed 10 micro-lessons',
    icon: '📚',
    category: 'learning',
    unlocked: false,
    requirement: 'Complete 10 micro-lessons'
  },
  {
    id: 'lesson-15',
    name: 'Dedicated Learner',
    description: 'Completed 15 micro-lessons',
    icon: '📖',
    category: 'learning',
    unlocked: false,
    requirement: 'Complete 15 micro-lessons'
  },
  {
    id: 'lesson-20',
    name: 'Scholar Rising',
    description: 'Completed 20 micro-lessons',
    icon: '🎓',
    category: 'learning',
    unlocked: false,
    requirement: 'Complete 20 micro-lessons'
  },
  {
    id: 'lesson-30',
    name: 'Wisdom Keeper',
    description: 'Completed 30 micro-lessons',
    icon: '📝',
    category: 'learning',
    unlocked: false,
    requirement: 'Complete 30 micro-lessons'
  },
  // Category-specific badges
  {
    id: 'hormone-scholar',
    name: 'Hormone Scholar',
    description: 'Completed all Hormone Essentials lessons',
    icon: '🧬',
    category: 'completion',
    unlocked: false,
    requirement: 'Complete Hormone Essentials category'
  },
  {
    id: 'symptom-detective',
    name: 'Symptom Detective',
    description: 'Completed all Symptom Mastery lessons',
    icon: '🎯',
    category: 'completion',
    unlocked: false,
    requirement: 'Complete Symptom Mastery category'
  },
  {
    id: 'nutrition-guru',
    name: 'Nutrition Guru',
    description: 'Mastered nutrition essentials',
    icon: '🥗',
    category: 'completion',
    unlocked: false,
    requirement: 'Complete nutrition lessons'
  },
  {
    id: 'movement-champion',
    name: 'Movement Champion',
    description: 'Learned exercise essentials',
    icon: '💪',
    category: 'completion',
    unlocked: false,
    requirement: 'Complete exercise lessons'
  },
  {
    id: 'calm-master',
    name: 'Calm Master',
    description: 'Mastered stress management',
    icon: '🧘',
    category: 'completion',
    unlocked: false,
    requirement: 'Complete stress management'
  },
  {
    id: 'sleep-champion',
    name: 'Sleep Champion',
    description: 'Mastered sleep strategies',
    icon: '😴',
    category: 'completion',
    unlocked: false,
    requirement: 'Complete sleep lessons'
  },
  {
    id: 'treatment-explorer',
    name: 'Treatment Explorer',
    description: 'Explored all treatment options',
    icon: '💊',
    category: 'completion',
    unlocked: false,
    requirement: 'Complete treatment lessons'
  },
  {
    id: 'mindfulness-maven',
    name: 'Mindfulness Maven',
    description: 'Completed mental wellness lessons',
    icon: '🧘‍♀️',
    category: 'completion',
    unlocked: false,
    requirement: 'Complete mental wellness'
  },
  // Streak badges
  {
    id: 'week-warrior',
    name: 'Week Wonder',
    description: 'Maintained a 7-day learning streak',
    icon: '🔥',
    category: 'streak',
    unlocked: false,
    requirement: '7-day streak'
  },
  {
    id: 'month-master',
    name: 'Momentum Maven',
    description: 'Maintained a 30-day learning streak',
    icon: '⭐',
    category: 'streak',
    unlocked: false,
    requirement: '30-day streak'
  },
  {
    id: 'two-month-star',
    name: 'Consistency Queen',
    description: 'Maintained a 60-day learning streak',
    icon: '✨',
    category: 'streak',
    unlocked: false,
    requirement: '60-day streak'
  },
  // Quiz badges
  {
    id: 'quiz-queen',
    name: 'Quiz Queen',
    description: 'Scored 100% on any quiz',
    icon: '👑',
    category: 'quiz',
    unlocked: false,
    requirement: 'Perfect quiz score'
  },
  {
    id: 'quiz-master',
    name: 'Quiz Master',
    description: 'Scored 100% on 5 quizzes',
    icon: '🏅',
    category: 'quiz',
    unlocked: false,
    requirement: '5 perfect quiz scores'
  },
  // Legacy badges (for existing learning paths)
  {
    id: 'first-steps',
    name: 'First Steps',
    description: 'Completed your first lesson',
    icon: '🌱',
    category: 'learning',
    unlocked: false,
    requirement: 'Complete 1 lesson'
  },
  {
    id: 'hrt-champion',
    name: 'HRT Champion',
    description: 'Completed the HRT Deep Dive path',
    icon: '💊',
    category: 'completion',
    unlocked: false,
    requirement: 'Complete HRT Deep Dive path'
  },
  {
    id: 'sleep-specialist',
    name: 'Sleep Specialist',
    description: 'Completed the Sleep & Fatigue path',
    icon: '😴',
    category: 'completion',
    unlocked: false,
    requirement: 'Complete Sleep & Fatigue path'
  },
  {
    id: 'nutrition-ninja',
    name: 'Nutrition Maven',
    description: 'Completed the Nutrition path',
    icon: '🥗',
    category: 'completion',
    unlocked: false,
    requirement: 'Complete Nutrition path'
  },
  {
    id: 'heart-hero',
    name: 'Heart Hero',
    description: 'Completed the Heart Health path',
    icon: '❤️',
    category: 'completion',
    unlocked: false,
    requirement: 'Complete Heart Health path'
  },
  {
    id: 'wellness-warrior',
    name: 'Wellness Champion',
    description: 'Completed all learning paths',
    icon: '🏆',
    category: 'completion',
    unlocked: false,
    requirement: 'Complete all paths'
  },
  {
    id: 'ultimate-scholar',
    name: 'Ultimate Scholar',
    description: 'Completed ALL lessons (paths + micro-lessons)',
    icon: '🌟',
    category: 'completion',
    unlocked: false,
    requirement: 'Complete everything'
  }
];

const initialStreak: StreakData = {
  currentStreak: 0,
  longestStreak: 0,
  lastActivityDate: '',
  totalDaysActive: 0
};

export const useGamification = () => {
  const [state, setState] = useState<GamificationState>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsedState = JSON.parse(saved);
      // Migrate old badge names to new ones
      const updatedBadges = parsedState.badges.map((badge: Badge) => {
        const currentBadge = initialBadges.find(b => b.id === badge.id);
        if (currentBadge) {
          return {
            ...badge,
            name: currentBadge.name,
            description: currentBadge.description,
            requirement: currentBadge.requirement
          };
        }
        return badge;
      });
      return {
        ...parsedState,
        badges: updatedBadges
      };
    }
    return {
      badges: initialBadges,
      progress: [],
      streak: initialStreak,
      quizScores: [],
      totalPoints: 0
    };
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const updateStreak = () => {
    const today = new Date().toDateString();
    const lastActivity = state.streak.lastActivityDate;

    if (lastActivity === today) {
      return; // Already logged today
    }

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toDateString();

    setState(prev => {
      const newStreak = {
        ...prev.streak,
        lastActivityDate: today,
        totalDaysActive: prev.streak.totalDaysActive + 1,
        currentStreak: lastActivity === yesterdayStr 
          ? prev.streak.currentStreak + 1 
          : 1,
        longestStreak: Math.max(
          prev.streak.longestStreak,
          lastActivity === yesterdayStr ? prev.streak.currentStreak + 1 : 1
        )
      };

      // Check for streak badges
      const updatedBadges = prev.badges.map(badge => {
        if (badge.id === 'week-warrior' && newStreak.currentStreak >= 7 && !badge.unlocked) {
          return { ...badge, unlocked: true, unlockedAt: new Date() };
        }
        if (badge.id === 'month-master' && newStreak.currentStreak >= 30 && !badge.unlocked) {
          return { ...badge, unlocked: true, unlockedAt: new Date() };
        }
        return badge;
      });

      // Check for 60-day streak badge
      const updatedBadges2 = updatedBadges.map(badge => {
        if (badge.id === 'two-month-star' && newStreak.currentStreak >= 60 && !badge.unlocked) {
          return { ...badge, unlocked: true, unlockedAt: new Date() };
        }
        return badge;
      });

      return {
        ...prev,
        streak: newStreak,
        badges: updatedBadges2,
        totalPoints: prev.totalPoints + 5 // Reduced from 10 to 5
      };
    });
  };

  const completeLesson = (pathId: string, pathName: string, lessonId: string, totalLessonsInPath: number) => {
    updateStreak();

    setState(prev => {
      const existingProgress = prev.progress.find(p => p.pathId === pathId);
      let updatedProgress: LearningProgress[];

      if (existingProgress) {
        updatedProgress = prev.progress.map(p => 
          p.pathId === pathId 
            ? {
                ...p,
                completedLessons: Array.from(new Set([...p.completedLessons, lessonId])),
                lastAccessed: new Date()
              }
            : p
        );
      } else {
        updatedProgress = [
          ...prev.progress,
          {
            pathId,
            pathName,
            completedLessons: [lessonId],
            totalLessons: totalLessonsInPath,
            lastAccessed: new Date()
          }
        ];
      }

      const currentPathProgress = updatedProgress.find(p => p.pathId === pathId);
      const totalLessonsCompleted = updatedProgress.reduce((sum, p) => sum + p.completedLessons.length, 0);
      const pathCompleted = currentPathProgress && currentPathProgress.completedLessons.length >= currentPathProgress.totalLessons;
      const allPathsCompleted = updatedProgress.every(p => p.completedLessons.length >= p.totalLessons) && updatedProgress.length >= 5;

      // Update badges
      const updatedBadges = prev.badges.map(badge => {
        if (badge.id === 'first-steps' && totalLessonsCompleted >= 1 && !badge.unlocked) {
          return { ...badge, unlocked: true, unlockedAt: new Date() };
        }
        if (badge.id === 'knowledge-seeker' && totalLessonsCompleted >= 10 && !badge.unlocked) {
          return { ...badge, unlocked: true, unlockedAt: new Date() };
        }
        if (badge.id === 'hrt-champion' && pathId === 'hrt' && pathCompleted && !badge.unlocked) {
          return { ...badge, unlocked: true, unlockedAt: new Date() };
        }
        if (badge.id === 'sleep-specialist' && pathId === 'sleep' && pathCompleted && !badge.unlocked) {
          return { ...badge, unlocked: true, unlockedAt: new Date() };
        }
        if (badge.id === 'nutrition-ninja' && pathId === 'nutrition' && pathCompleted && !badge.unlocked) {
          return { ...badge, unlocked: true, unlockedAt: new Date() };
        }
        if (badge.id === 'heart-hero' && pathId === 'heart' && pathCompleted && !badge.unlocked) {
          return { ...badge, unlocked: true, unlockedAt: new Date() };
        }
        if (badge.id === 'wellness-warrior' && allPathsCompleted && !badge.unlocked) {
          return { ...badge, unlocked: true, unlockedAt: new Date() };
        }
        return badge;
      });

      return {
        ...prev,
        progress: updatedProgress,
        badges: updatedBadges,
        totalPoints: prev.totalPoints + 10 // Reduced from 15 to 10
      };
    });
  };

  const completeMicroLesson = (lessonId: string, categoryId: string) => {
    updateStreak();

    setState(prev => {
      // Track completed micro-lessons
      const completedMicroLessons = (prev as any).completedMicroLessons || [];
      if (completedMicroLessons.includes(lessonId)) {
        return prev; // Already completed
      }

      const newCompletedMicroLessons = [...completedMicroLessons, lessonId];
      const totalMicroLessons = newCompletedMicroLessons.length;

      // Update badges based on micro-lesson count
      let updatedBadges = prev.badges.map(badge => {
        if (badge.id === 'first-lesson' && totalMicroLessons >= 1 && !badge.unlocked) {
          return { ...badge, unlocked: true, unlockedAt: new Date() };
        }
        if (badge.id === 'lesson-3' && totalMicroLessons >= 3 && !badge.unlocked) {
          return { ...badge, unlocked: true, unlockedAt: new Date() };
        }
        if (badge.id === 'lesson-5' && totalMicroLessons >= 5 && !badge.unlocked) {
          return { ...badge, unlocked: true, unlockedAt: new Date() };
        }
        if (badge.id === 'lesson-10' && totalMicroLessons >= 10 && !badge.unlocked) {
          return { ...badge, unlocked: true, unlockedAt: new Date() };
        }
        if (badge.id === 'lesson-15' && totalMicroLessons >= 15 && !badge.unlocked) {
          return { ...badge, unlocked: true, unlockedAt: new Date() };
        }
        if (badge.id === 'lesson-20' && totalMicroLessons >= 20 && !badge.unlocked) {
          return { ...badge, unlocked: true, unlockedAt: new Date() };
        }
        if (badge.id === 'lesson-30' && totalMicroLessons >= 30 && !badge.unlocked) {
          return { ...badge, unlocked: true, unlockedAt: new Date() };
        }
        return badge;
      });

      // Check for category completion badges
      const categoryLessonIds: Record<string, string[]> = (prev as any).categoryLessonIds || {};
      if (!categoryLessonIds[categoryId]) {
        categoryLessonIds[categoryId] = [];
      }
      if (!categoryLessonIds[categoryId].includes(lessonId)) {
        categoryLessonIds[categoryId].push(lessonId);
      }

      return {
        ...prev,
        completedMicroLessons: newCompletedMicroLessons,
        categoryLessonIds,
        badges: updatedBadges,
        totalPoints: prev.totalPoints + 5 // Reduced from 10 to 5
      };
    });
  };

  const unlockCategoryBadge = (badgeId: string) => {
    setState(prev => {
      const updatedBadges = prev.badges.map(badge => {
        if (badge.id === badgeId && !badge.unlocked) {
          return { ...badge, unlocked: true, unlockedAt: new Date() };
        }
        return badge;
      });

      return {
        ...prev,
        badges: updatedBadges
      };
    });
  };

  const submitQuizScore = (quizId: string, quizName: string, score: number, totalQuestions: number) => {
    updateStreak();

    setState(prev => {
      const newQuizScore: QuizScore = {
        quizId,
        quizName,
        score,
        totalQuestions,
        completedAt: new Date()
      };

      const isPerfectScore = score === totalQuestions;

      const perfectScoreCount = prev.quizScores.filter(q => q.score === q.totalQuestions).length + (isPerfectScore ? 1 : 0);

      const updatedBadges = prev.badges.map(badge => {
        if (badge.id === 'quiz-queen' && isPerfectScore && !badge.unlocked) {
          return { ...badge, unlocked: true, unlockedAt: new Date() };
        }
        if (badge.id === 'quiz-master' && perfectScoreCount >= 5 && !badge.unlocked) {
          return { ...badge, unlocked: true, unlockedAt: new Date() };
        }
        return badge;
      });

      return {
        ...prev,
        quizScores: [...prev.quizScores, newQuizScore],
        badges: updatedBadges,
        totalPoints: prev.totalPoints + (score * 5) // Reduced from score * 10 to score * 5
      };
    });
  };

  const resetProgress = () => {
    setState({
      badges: initialBadges,
      progress: [],
      streak: initialStreak,
      quizScores: [],
      totalPoints: 0
    });
  };

  return {
    ...state,
    completeLesson,
    completeMicroLesson,
    unlockCategoryBadge,
    submitQuizScore,
    resetProgress,
    updateStreak
  };
};
